import { colors } from '@/theme/foundations';
import {
  PricingStatus,
  QuoteDocumentStatus,
  QuoteDocumentType,
  QuoteRunStatus,
} from '@/types/quoteDocuments';

export const QUOTE_DOCUMENTS_PAGE_SIZE = 20;
export const QUOTE_DOCUMENTS_STALE_TIME = 30_000;
export const QUOTE_DOCUMENTS_GC_TIME = 5 * 60 * 1000;

/** Matches Case and Team detail reading width. */
export const QUOTE_DOCUMENT_READING_MAX_W = '1100px';
/** Long-form Commercial Narrative measure (~60–75 characters). */
export const QUOTE_DOCUMENT_PROSE_MAX_W = '65ch';

export const QUOTE_DOCUMENTS_QUERY_OPTIONS = {
  staleTime: QUOTE_DOCUMENTS_STALE_TIME,
  gcTime: QUOTE_DOCUMENTS_GC_TIME,
};

export const QUOTE_DOCUMENTS_QUERY_KEY = 'quote-documents';
export const QUOTE_DOCUMENT_QUERY_KEY = 'quote-document';
export const QUOTE_DOCUMENT_ACTIVITY_QUERY_KEY = 'quote-document-activity';
export const QUOTE_DOCUMENT_ACTIVITY_PAGE_SIZE = 20;
export const QUOTE_DOCUMENT_REVISIONS_QUERY_KEY = 'quote-document-revisions';

export const QUOTE_DOCUMENT_REVISION_STATUS_LABELS: Record<string, string> = {
  issued: 'Checkpoint',
  accepted: 'Accepted',
  superseded: 'Superseded',
};

/** Local recovery drafts for Commercial Narrative editing. */
export const QUOTE_DOCUMENT_RECOVERY_PREFIX = 'qd-recovery:';
/** Short expiry — recover after reload/crash, not indefinitely. */
export const QUOTE_DOCUMENT_RECOVERY_TTL_MS = 24 * 60 * 60 * 1000;

/** JWT v3 gateway group — same surface as Cases. */
export const QUOTE_DOCUMENTS_API_BASE =
  'https://api.vh3connect.io/api:f-Fs4_ed/quoting/documents';

/** Dedicated quoting autocomplete — not entity `/search/autocomplete`. */
export const PARTS_CATALOG_AUTOCOMPLETE_API =
  'https://api.vh3connect.io/api:f-Fs4_ed/quoting/parts-catalog/autocomplete';
export const PARTS_CATALOG_AUTOCOMPLETE_QUERY_KEY =
  'parts-catalog-autocomplete';
export const PARTS_CATALOG_AUTOCOMPLETE_DEBOUNCE_MS = 300;
export const PARTS_CATALOG_AUTOCOMPLETE_MIN_CHARS = 2;
export const PARTS_CATALOG_AUTOCOMPLETE_LIMIT = 10;
export const PARTS_CATALOG_AUTOCOMPLETE_STALE_TIME_MS = 15_000;

export const QUOTE_DOCUMENT_STATUS_LABELS: Record<QuoteDocumentStatus, string> =
  {
    draft: 'Draft',
    approved: 'Approved',
    accepted: 'Accepted',
    declined: 'Declined',
  };

export const QUOTE_DOCUMENT_STATUS_COLORS: Record<QuoteDocumentStatus, string> =
  {
    draft: colors.gray[300],
    approved: colors.alternative.blue,
    accepted: colors.alternative.green,
    declined: colors.alternative.red,
  };

export const QUOTE_RUN_STATUS_LABELS: Record<QuoteRunStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
};

export const isQuoteRunInFlight = (
  runStatus: QuoteRunStatus | string | null | undefined
): boolean => runStatus === 'queued' || runStatus === 'running';

export const QUOTE_RUN_STATUS_COLORS: Record<QuoteRunStatus, string> = {
  queued: colors.gray[300],
  running: colors.alternative.purple,
  completed: colors.alternative.green,
  failed: colors.alternative.red,
};

export const QUOTE_DOCUMENT_TYPE_LABELS: Record<QuoteDocumentType, string> = {
  follow_on: 'Follow-on',
  site_survey: 'Site survey',
  ad_hoc: 'Ad hoc',
};

export const PRICING_STATUS_LABELS: Record<PricingStatus, string> = {
  priced: 'Priced',
  poa: 'POA',
  tbc: 'TBC',
  indicative: 'Indicative',
};

export const PRICING_STATUS_COLORS: Record<PricingStatus, string> = {
  priced: colors.alternative.green,
  poa: colors.alternative.gold,
  tbc: colors.alternative.purple,
  indicative: colors.alternative.blue,
};

export const QUOTE_DOCUMENT_ACTIVITY_LABELS: Record<string, string> = {
  created: 'Created',
  draft_updated: 'Draft updated',
  agent_patch_applied: 'Agent patch applied',
  repriced: 'Repriced',
  version_created: 'Version created',
  superseded: 'Superseded',
  approved: 'Approved',
  accepted: 'Accepted',
  declined: 'Declined',
  expired: 'Expired',
  job_link_changed: 'Job link changed',
  pushed_to_fms: 'Pushed to FMS',
  notified: 'Notified',
  reopened: 'Reopened',
  duplicated: 'Duplicated',
  archived: 'Archived',
};
